import React, { useState, useEffect } from 'react';
import { ClipboardCheck, Save, Search } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ExportMenu from '../common/ExportMenu';
import './AttendancePage.css';

export default function AttendancePage() {
  const { user } = useAuth();
  const [selectedClass, setSelectedClass] = useState(5);
  const [selectedSection, setSelectedSection] = useState('A');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0 });

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Load existing attendance records
  const loadExistingAttendance = async (classNum, section, date) => {
    try {
      const response = await api.get(`/attendance?class=${classNum}&section=${section}&date=${date}`);
      const existingAttendance = {};
      response.data.forEach(record => {
        existingAttendance[record.studentId] = record.status;
      });
      return existingAttendance;
    } catch (error) {
      console.error('Error loading existing attendance:', error);
      return {};
    }
  };

  // Load students for selected class and section
  const handleLoadStudents = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Fetch students
      const studentsResponse = await api.get(
        `/students?class=${selectedClass}&section=${selectedSection}&limit=100`
      );
      const studentsList = studentsResponse.data.students || studentsResponse.data || [];
      setStudents(studentsList);

      // Load existing attendance
      const existingAttendance = await loadExistingAttendance(
        selectedClass,
        selectedSection,
        selectedDate
      );

      // Initialize attendance with existing records or default to 'present'
      const initialAttendance = {};
      studentsList.forEach(student => {
        initialAttendance[student.id] = existingAttendance[student.id] || 'present';
      });

      setAttendance(initialAttendance);
      updateStats(initialAttendance);
      setMessage({ type: 'success', text: `Loaded ${studentsList.length} students` });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load students'
      });
      setStudents([]);
      setAttendance({});
    } finally {
      setLoading(false);
    }
  };

  // Update attendance status for a student
  const handleAttendanceChange = (studentId, status) => {
    const updatedAttendance = {
      ...attendance,
      [studentId]: status
    };
    setAttendance(updatedAttendance);
    updateStats(updatedAttendance);
  };

  // Calculate and update statistics
  const updateStats = (attendanceData) => {
    let present = 0, absent = 0, late = 0;
    Object.values(attendanceData).forEach(status => {
      if (status === 'present') present++;
      else if (status === 'absent') absent++;
      else if (status === 'late') late++;
    });
    setStats({ present, absent, late });
  };

  // Save attendance records
  const handleSaveAttendance = async () => {
    if (students.length === 0) {
      setMessage({ type: 'error', text: 'No students loaded' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const records = students.map(student => ({
        studentId: student.id,
        class: selectedClass,
        section: selectedSection,
        date: selectedDate,
        status: attendance[student.id] || 'present'
      }));

      await api.post('/attendance/bulk', { records });
      setMessage({ type: 'success', text: 'Attendance saved successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save attendance'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle date change and reload attendance
  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    if (students.length > 0) {
      const existingAttendance = await loadExistingAttendance(selectedClass, selectedSection, newDate);
      const initialAttendance = {};
      students.forEach(student => {
        initialAttendance[student.id] = existingAttendance[student.id] || 'present';
      });
      setAttendance(initialAttendance);
      updateStats(initialAttendance);
    }
  };

  return (
    <div className="attendance-page">
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>
            <ClipboardCheck size={28} />
            Student Attendance
          </h1>
          <ExportMenu
            endpoint="/attendance/export"
            role={user?.role}
            baseName="attendance"
            scopes={[
              {
                id: 'class',
                label: 'This class',
                hint: `Class ${selectedClass} — Section ${selectedSection}`,
                params: {
                  class: selectedClass,
                  section: selectedSection,
                  ...(selectedDate ? { startDate: selectedDate, endDate: selectedDate } : {})
                }
              },
              {
                id: 'school',
                label: 'Whole school',
                hint: 'All classes · all time',
                params: {}
              }
            ]}
          />
        </div>

        <div className="card-body">
          {/* Stats Section */}
          {students.length > 0 && (
            <div className="stats-grid">
              <div className="stat-card stat-present">
                <div className="stat-value">{stats.present}</div>
                <div className="stat-label">Present</div>
              </div>
              <div className="stat-card stat-absent">
                <div className="stat-value">{stats.absent}</div>
                <div className="stat-label">Absent</div>
              </div>
              <div className="stat-card stat-late">
                <div className="stat-value">{stats.late}</div>
                <div className="stat-label">Late</div>
              </div>
            </div>
          )}

          {/* Message Display */}
          {message.text && (
            <div className={`message message-${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-group">
              <label htmlFor="class-select">Class</label>
              <select
                id="class-select"
                className="form-control"
                value={selectedClass}
                onChange={(e) => setSelectedClass(Number(e.target.value))}
                disabled={loading}
              >
                {Array.from({ length: 6 }, (_, i) => i + 5).map(classNum => (
                  <option key={classNum} value={classNum}>Class {classNum}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="section-select">Section</label>
              <select
                id="section-select"
                className="form-control"
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                disabled={loading}
              >
                <option value="A">Section A</option>
                <option value="B">Section B</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="date-input">Date</label>
              <input
                id="date-input"
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                disabled={loading}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleLoadStudents}
              disabled={loading}
            >
              <Search size={18} />
              Load Students
            </button>
          </div>

          {/* Attendance Grid */}
          {students.length > 0 && (
            <div className="attendance-grid">
              {students.map(student => (
                <div key={student.id} className="attendance-row">
                  <div className="attendance-student">
                    <div className="student-roll">{student.rollNo || '-'}</div>
                    <div className="student-name">{student.name}</div>
                  </div>

                  <div className="attendance-options">
                    <button
                      className={`attendance-btn present ${attendance[student.id] === 'present' ? 'active' : ''}`}
                      onClick={() => handleAttendanceChange(student.id, 'present')}
                    >
                      Present
                    </button>
                    <button
                      className={`attendance-btn absent ${attendance[student.id] === 'absent' ? 'active' : ''}`}
                      onClick={() => handleAttendanceChange(student.id, 'absent')}
                    >
                      Absent
                    </button>
                    <button
                      className={`attendance-btn late ${attendance[student.id] === 'late' ? 'active' : ''}`}
                      onClick={() => handleAttendanceChange(student.id, 'late')}
                    >
                      Late
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Save Button */}
          {students.length > 0 && (
            <div className="action-bar">
              <button
                className="btn btn-success"
                onClick={handleSaveAttendance}
                disabled={loading}
              >
                <Save size={18} />
                Save Attendance
              </button>
            </div>
          )}

          {students.length === 0 && !message.text && (
            <div className="empty-state">
              <ClipboardCheck size={48} />
              <p>Select class, section, and date, then click "Load Students" to begin marking attendance.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
